/**
 * The auction engine's live feed — protocol 2 (2026-10-08).
 *
 * ONE contract, imported by the sender (`workers/engine`) and the receiver
 * (`packages/api-client`, used by the website and both apps), so the two
 * cannot drift. Design: `docs/engine-live-work-design-2026-10-08.md` §1–2.
 *
 * What protocol 2 changes from protocol 1 (the 5-second "pulse"):
 *   - No pulse. Protocol 1 sent EVERY lot to EVERY watcher every 5 s — 136 KB
 *     a pulse on a 535-lot sale, ~95% of Stress 1's 19.6 GB feed.
 *   - A socket says which lots it cares about: the lots ON SCREEN (`view`), the
 *     lots it WATCHES (`watch`), and — for a signed-in bidder — the lots it has
 *     bid on (the engine knows those itself). It hears about only those lots.
 *     House staff hear about every lot.
 *   - A changed lot is sent as one small `lot` message (its whole current view,
 *     not a diff — idempotent, order-safe by `seq`).
 *   - Countdowns run on the device from `close_time` and the server-time offset
 *     in `ack`; the engine sends a lot again only when its clock moves.
 *   - One `summary` of every lot, once, when a page asks for it (list pages).
 *
 * Sequence numbers are the engine's per-sale chain positions, sent as decimal
 * STRINGS (they are bigints in the engine). A receiver ignores a `lot` whose
 * `seq` is not newer than what it already holds for that lot — except that
 * the full view of a change replaces a `summary` line of the same change.
 */

export const FEED_PROTOCOL = 2 as const;

// ─── Shared shapes ─────────────────────────────────────────────────────────

/** Mirrors the engine's `LotState` (workers/engine/src/types/state.ts). */
export type FeedLotState =
  | 'pending'
  | 'open'
  | 'bin_pending'
  | 'closing'
  | 'closed_sold'
  | 'closed_no_winner'
  | 'closed_bin_sold'
  | 'closed_no_sale'
  | 'canceled';

/** Who the socket is, as the engine bound it at upgrade (never self-declared). */
export type FeedRole = 'public_observer' | 'bidder' | 'staff';

/**
 * One lot as a page renders it. Built by the engine per audience: fields a
 * house hides from the public (its privacy flags) arrive as null for that
 * audience — null means "hidden or none", never zero.
 */
export interface LotView {
  lot_id: string;
  /** Stable position of the lot in the sale (assigned once, never reused). */
  ordinal: number;
  state: FeedLotState;
  current_high_cents: number | null;
  /** The engine's minimum acceptable next bid (its increment rules) — clients never re-derive it. */
  next_min_cents: number | null;
  /** Accepted bids on the lot; null when the house hides bid history from this audience. */
  bid_count: number | null;
  reserve_met: boolean | null;
  /** The lot's current close time, ISO 8601 — moves on every anti-snipe extension. */
  close_time: string | null;
  /** ISO 8601 of the last accepted bid; null when none or hidden. */
  last_bid_at: string | null;
  /**
   * The engine sequence of the last change to how this lot reads (decimal
   * string): the last event on the lot, or a sale-wide rules change after it
   * (the house's increments or privacy switches change `next_min_cents` and
   * what is hidden on every lot at once), whichever is later. A view that
   * changed always carries a newer seq, so "ignore a lot that is not newer"
   * never drops a real change.
   */
  seq: string;
  /** Present only for house staff. */
  staff?: LotStaffView;
}

export interface LotStaffView {
  leader_pseudonym: string | null;
  second_high_cents: number | null;
  reserve_cents: number | null;
  /** How many bidders hold a ceiling on the lot. */
  ceiling_count: number;
}

/** The compact per-lot line of a `summary` (list pages; sent once). */
export interface LotSummary {
  lot_id: string;
  ordinal: number;
  state: FeedLotState;
  current_high_cents: number | null;
  bid_count: number | null;
  close_time: string | null;
  /**
   * The engine sequence of the last change to THIS lot — the same value its
   * `LotView.seq` carries. Added 2026-10-08 (client build): the summary's own
   * `seq` is sale-wide, so without this a receiver cannot order a summary line
   * against a `lot` message for the same lot. It either drops the fuller view
   * that arrives just after the summary (a lot page loses its next minimum) or
   * lays an older view over a newer line.
   */
  seq: string;
}

export type FeedSaleState = 'scheduled' | 'live' | 'closing' | 'closed' | 'paused';

// ─── Client → engine ───────────────────────────────────────────────────────

export interface SubscribeMessage {
  type: 'subscribe';
  protocol: typeof FEED_PROTOCOL;
  /** Lot ids on screen right now. */
  view?: string[];
  /** Lot ids the viewer watches (their watchlist, for this sale). */
  watch?: string[];
  /** Send the sale-wide compact bid ticker (the Lots page ticker). */
  ticker?: boolean;
  /** Send one `summary` of every lot (list pages). */
  summary?: boolean;
  /**
   * Resume after a reconnect: replay changes FROM this sequence, INCLUSIVE
   * (as protocol 1's `replayFrom` already does). The client sends the highest
   * sequence it applied. One change can reach a socket as several messages
   * with the same sequence (`you`, `lot`, `ticker`, `event`); a socket that
   * dropped between them still gets the rest, and the client ignores what it
   * already holds. Exclusive replay would lose them for good.
   */
  replay_from_seq?: string;
}

/** Replaces the view set (the viewer scrolled). The engine answers with a `lot` for each lot newly in view. */
export interface ViewMessage {
  type: 'view';
  lots: string[];
}

/** Replaces the watched set. */
export interface WatchMessage {
  type: 'watch';
  lots: string[];
}

export interface PingMessage {
  type: 'ping';
}

export type ClientMessage = SubscribeMessage | ViewMessage | WatchMessage | PingMessage;

// ─── Engine → client ───────────────────────────────────────────────────────

export interface AckMessage {
  type: 'ack';
  protocol: typeof FEED_PROTOCOL;
  role: FeedRole;
  current_state_seq: string;
  /** Engine clock, epoch ms — the device's countdown offset. */
  server_time: number;
  /**
   * The Cloudflare data centre this connection came in through, as Cloudflare
   * names it (an airport code, e.g. `"ORD"`) — 2026-10-08, for diagnosis:
   * Stress 1 saw Cloudflare move its traffic between data centres mid-run and
   * nothing on the connection said so. Absent when the engine was not told
   * (a request that did not come through Cloudflare's edge). Informational
   * only: a client must not change behaviour on it.
   */
  colo?: string;
}

export interface SubscribedMessage {
  type: 'subscribed';
  current_state_seq: string;
  sale_state: FeedSaleState;
}

export interface SummaryMessage {
  type: 'summary';
  seq: string;
  lots: LotSummary[];
}

export interface LotMessage {
  type: 'lot';
  seq: string;
  lot: LotView;
}

/**
 * The bidder's own standing on one lot changed. Sent only to that bidder.
 *
 * Ordering (2026-10-08, client build) — the client tells its own bid from a
 * rival's by this message, not by a time window, so:
 *   - `seq` is the sequence of the change that moved the standing;
 *   - for one change, the engine sends this socket's `you` BEFORE the `lot`
 *     message carrying the same change (whose `seq` equals it, or is later
 *     when the same change also extended the lot's clock);
 *   - after `subscribe`, the engine sends one `you` for every lot the bidder
 *     holds a ceiling on, so a page knows the bidder's standing on open
 *     without asking Postgres.
 */
export interface YouMessage {
  type: 'you';
  seq: string;
  lot_id: string;
  leading: boolean;
  /** Their own maximum (proxy ceiling) on the lot; null when none. */
  ceiling_cents: number | null;
  /** True on the change that took the lead away from them. */
  outbid: boolean;
}

/**
 * One accepted bid, sale-wide, to sockets that asked (`ticker: true`). Not
 * sent to non-staff when the house hides its bid history or its winning info
 * (`amount_cents` is another bidder's amount and cannot be blank), nor on a
 * sealed or Vickrey sale.
 */
export interface TickerMessage {
  type: 'ticker';
  seq: string;
  lot_id: string;
  ordinal: number;
  amount_cents: number;
  at: string;
  /**
   * "Was this bid mine?" (2026-10-08) — present ONLY on a `bidder` socket:
   * true when this bid is the socket's own. Never sent to public observers or
   * staff. Additive: a client that ignores it keeps working.
   */
  mine?: boolean;
}

export interface SaleMessage {
  type: 'sale';
  seq: string;
  state: FeedSaleState;
}

/** Mirrors the engine's `MilestoneKind` (workers/engine/src/lib/milestone-detector.ts). */
export type FeedMilestoneKind =
  | 'first_bid'
  | 'bid_count_10'
  | 'bid_count_25'
  | 'bid_count_50'
  | 'bid_count_100'
  | 'bid_count_250'
  | 'bid_count_500'
  | 'reserve_met';

/**
 * A lot in the socket's interest crossed a milestone (W17 — UI animation and
 * sound cues). Ephemeral, not on the chain. Counted milestones (`first_bid`,
 * `bid_count_*`) are withheld from non-staff when the house hides its bid
 * history; every milestone is withheld from non-staff on a sealed or Vickrey
 * lot before it closes.
 */
export interface MilestoneMessage {
  type: 'milestone';
  kind: FeedMilestoneKind;
  lot_id: string;
  /** The count, or for `reserve_met` the price that met it. */
  value: number;
  /** The client bid id that triggered it (a client-generated id, never an identity). */
  triggered_by_bid_id: string;
  /** ISO 8601 of the triggering bid. */
  ts: string;
}

/**
 * A bid-history event for a lot in the socket's interest, through the engine's
 * visibility filter (same envelope protocol 1 used, hash fields stripped).
 */
export interface EventMessage {
  type: 'event';
  event_id: string;
  auction_id: string;
  lot_id: string | null;
  sequence_num: string;
  timestamp: string;
  event_type: string;
  source: string;
  actor: unknown;
  payload: unknown;
  /**
   * "Was this bid mine?" (2026-10-08) — present ONLY on a `bidder` socket and
   * only on a bid event (`bid_accepted`, `bid_proxy_executed`,
   * `bid_floor_recorded`, `bundle_bid_accepted`): true when the bid is the
   * socket's own, a proxy execution on its behalf included. Match bids by
   * this, not by `you.seq` (the LAST event of a change: a bid answered at once
   * by the leader's proxy is not the change's last event). Never sent to
   * public observers or staff. Additive: a client that ignores it keeps working.
   */
  mine?: boolean;
}

export interface ResyncMessage {
  type: 'resync';
  reason: 'replay_window_exceeded' | 'protocol_changed' | 'state_rebuilt';
}

export interface PongMessage {
  type: 'pong';
}

export interface ErrorMessage {
  type: 'error';
  code: FeedErrorCode;
  message: string;
}

export type ServerMessage =
  | AckMessage
  | SubscribedMessage
  | SummaryMessage
  | LotMessage
  | YouMessage
  | TickerMessage
  | SaleMessage
  | MilestoneMessage
  | EventMessage
  | ResyncMessage
  | PongMessage
  | ErrorMessage;

// ─── Codes and limits ──────────────────────────────────────────────────────

export type FeedErrorCode =
  | 'bad_message'
  | 'protocol_unsupported'
  | 'too_many_lots'
  | 'unknown_lot';

/** WebSocket close codes the engine uses (4000–4999 are application codes). */
export const FEED_CLOSE = {
  /** The client sent something the engine could not read. */
  BAD_MESSAGE: 4400,
  /** The client speaks a protocol the engine no longer serves — reload. */
  PROTOCOL_UNSUPPORTED: 4409,
  /** The sale already holds the maximum number of sockets. */
  TOO_MANY_SOCKETS: 4429,
  /**
   * Nothing heard from this socket in the engine's idle window — no message
   * and no keep-alive ping (2026-10-08). Reconnect and replay, as after any
   * drop: a client that is really there reconnects at once.
   *
   * The window is the engine's `WEBSOCKET_IDLE_TIMEOUT_MS` (90 s: three
   * missed pings at `FEED_LIMITS.PING_INTERVAL_MS`). Stress 1 found the engine
   * never noticed a dead connection: open sockets climbed from 160 real
   * clients to 373, and ~3.7 GB of the feed went to connections nobody was
   * reading. A client that pings on schedule is never closed with this code.
   */
  IDLE: 4408,
  /** A send to this socket failed; reconnect and replay. */
  SEND_FAILED: 1011,
} as const;

export const FEED_LIMITS = {
  /** Most lots one socket may have in view at once. */
  MAX_VIEW_LOTS: 500,
  /** Most lots one socket may watch in one sale. */
  MAX_WATCH_LOTS: 1000,
  /** Most sockets one sale's engine accepts. */
  MAX_SOCKETS_PER_SALE: 5000,
  /**
   * A client pings this often (ms); the engine auto-answers without waking.
   * The ping is also how the engine knows the client is still there: a socket
   * that sends nothing — no ping, no message — for the engine's idle window
   * (three intervals) is closed `FEED_CLOSE.IDLE`.
   */
  PING_INTERVAL_MS: 30_000,
} as const;

// ─── Type guards (each side validates what it receives) ───────────────────

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/**
 * A sequence number on the wire: a CANONICAL decimal string — digits, no sign,
 * no leading zero (except `"0"` itself), as a bigint's `toString()` writes it.
 * `isNewerSeq` orders by length first, which is only right when no value is
 * padded: `"007"` would otherwise rank above `"10"`.
 */
export const isSeq = (v: unknown): v is string => typeof v === 'string' && /^(0|[1-9]\d*)$/.test(v);

/** Engine side: is this a well-formed client message? (Never trusts role or identity from it.) */
export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.type) {
    case 'subscribe':
      if (m.protocol !== FEED_PROTOCOL) return null;
      if (m.view !== undefined && !isStringArray(m.view)) return null;
      if (m.watch !== undefined && !isStringArray(m.watch)) return null;
      if (m.ticker !== undefined && typeof m.ticker !== 'boolean') return null;
      if (m.summary !== undefined && typeof m.summary !== 'boolean') return null;
      if (m.replay_from_seq !== undefined && !isSeq(m.replay_from_seq)) return null;
      return m as unknown as SubscribeMessage;
    case 'view':
    case 'watch':
      return isStringArray(m.lots) ? (m as unknown as ViewMessage | WatchMessage) : null;
    case 'ping':
      return { type: 'ping' };
    default:
      return null;
  }
}

/** Client side: is this a message the client knows how to apply? Unknown types are ignored, not errors. */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  switch (m.type) {
    case 'ack':
      return m.protocol === FEED_PROTOCOL &&
        isSeq(m.current_state_seq) &&
        typeof m.server_time === 'number' &&
        (m.colo === undefined || typeof m.colo === 'string')
        ? (m as unknown as AckMessage)
        : null;
    case 'lot':
      return isSeq(m.seq) && typeof m.lot === 'object' && m.lot !== null
        ? (m as unknown as LotMessage)
        : null;
    case 'summary':
      return isSeq(m.seq) && Array.isArray(m.lots) ? (m as unknown as SummaryMessage) : null;
    case 'you':
    case 'sale':
      return isSeq(m.seq) ? (m as unknown as ServerMessage) : null;
    case 'ticker':
      return isSeq(m.seq) && mineIsValid(m) ? (m as unknown as TickerMessage) : null;
    case 'subscribed':
      return isSeq(m.current_state_seq) ? (m as unknown as SubscribedMessage) : null;
    case 'milestone':
      return typeof m.lot_id === 'string' && typeof m.kind === 'string'
        ? (m as unknown as MilestoneMessage)
        : null;
    case 'event':
      return typeof m.sequence_num === 'string' && mineIsValid(m)
        ? (m as unknown as EventMessage)
        : null;
    case 'resync':
    case 'pong':
    case 'error':
      return m as unknown as ServerMessage;
    default:
      return null;
  }
}

/** `mine` is optional; when present it is a boolean. */
function mineIsValid(m: Record<string, unknown>): boolean {
  return m.mine === undefined || typeof m.mine === 'boolean';
}

/** True when `candidate` is strictly newer than `current` (decimal sequence strings). */
export function isNewerSeq(candidate: string, current: string | null | undefined): boolean {
  if (current === null || current === undefined) return true;
  if (candidate.length !== current.length) return candidate.length > current.length;
  return candidate > current;
}
